import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * Filter button class recipes.
 *
 * Ported from the Figma sets `Filter button` (2072:142028), `Filter panel` (2072:144893) and, for the
 * bar, `Filter bar` (2072:145520) on `Filter bar [Draft]`. Re-read 2026-09-29.
 *
 * ── Almost nothing here is new ────────────────────────────────────────────────────────────────
 *
 * The trigger is drawn by cloning the `Split button` set's Secondary cells, so it IS a secondary
 * `Button`: fill, rim, hover, pressed, focus ring, disabled and motion all come from `buttonRecipe`,
 * and the Clear cell is an `IconButton` in the same appearance. The pair is grouped by
 * `splitButtonRecipe`, which owns the 2px seam and the 4px inner corners, so the two cannot drift
 * from the Split button. What is left below is what a filter adds: two label colours, a truncating
 * value, and the panel's own rows.
 *
 * The panel sits in a `Popover` (Figma copied the popover's surface by hand; the code cannot, and
 * `Popover`'s own docs name "a filter panel" as a use). Its option rows read the SAME slots as
 * `MultiSelect` and `Select` rows — `--ds-select-item-*` and `--ds-checkbox-*` — so a retuned slot
 * moves them together.
 */

/**
 * The trigger's own additions to `buttonRecipe`.
 *
 * `[--ds-btn-content-shift]` is the split group's optical 2px nudge, applied whether or not the Clear
 * cell is there: the drawing keeps the trigger's 18/14 padding when it stands alone (a bare `Role`
 * is 92px, the same as when filled), so a symmetric box would land the label 2px left of the cell.
 *
 * `max-w-[320px]` is a cap the file cannot draw (a HUG text grows the button). The MUI trigger capped at
 * 200px, which is narrower than the filled text ("Role: Admin, Editor +1 more" is about 240px), so
 * this is 320.
 */
export const filterTriggerRecipe = cva(
  ['group/ft', 'max-w-[320px] min-w-0', '[--ds-btn-content-shift:--spacing(0.5)]'],
  {
    variants: {
      /**
       * The seam corners are set on the cell itself and NOT through `splitButtonRecipe`'s
       * `[&>:first-child]` rule. Base UI puts hidden focus-guard spans on either side of an open
       * popover's trigger, so while the panel is open the trigger is no longer the group's first child:
       * the rule lands on a span, the trigger falls back to a full pill, and the seam goes lopsided
       * (round on one side of it, square on the other). `--ds-split-radius` still comes from the group.
       */
      filled: {
        true: '[--ds-btn-radius:var(--ds-split-radius)_4px_4px_var(--ds-split-radius)]',
        false: '',
      },
    },
    defaultVariants: { filled: false },
  }
);

/** The Clear cell's half of the seam, set on the cell for the reason `filterTriggerRecipe` gives. */
export const filterClearRecipe = cva([
  '[--ds-btn-radius:4px_var(--ds-split-radius)_var(--ds-split-radius)_4px]',
  '[--ds-btn-content-shift:calc(--spacing(0.5)*-1)]',
]);

/**
 * The category and its colon, once something is picked.
 *
 * The set draws them a step below the value (`foreground/secondary`) and moves all three together on
 * hover. Active leaves them alone, which is why the hover rule excludes `:active` exactly as the
 * button's own does. A disabled trigger recolours the whole line to the button's own disabled slot.
 */
export const filterTriggerLabelRecipe = cva([
  'shrink-0',
  'text-[var(--foreground-secondary)]',
  'group-[:hover:not(:active):not([data-disabled])]/ft:text-[var(--foreground-secondary-hover)]',
  'group-data-[disabled]/ft:text-[var(--ds-btn-fg-disabled)]',
]);

/** The value. Truncates so a long pick cannot push the chevron out; the tooltip carries the rest. */
export const filterTriggerValueRecipe = cva(['min-w-0 truncate']);

/**
 * The panel's inner column. `ds-select` is what puts the option rows' `--ds-select-item-*` slots in
 * scope (`MultiSelect` puts it on its popup for the same reason); the surface itself is the
 * popover's.
 *
 * The 280px Figma draws is the panel's OUTER width, so `FilterButton` sets `w-[280px]` on the popover
 * panel and this column fills what is left (262px: 8px of padding and a 1px rim on each side). The
 * file's stroke sits inside its box and the popover's border sits outside its padding, so rows here
 * are 2px narrower than the drawing's 264. Every `Popover` is 1px further in than Figma for the same
 * reason.
 */
export const filterPanelRecipe = cva(['ds-select', 'flex w-full flex-col gap-1']);

/** The panel's outer width: Figma's 280px (no spacing step is that wide), capped to the viewport. */
export const filterPanelSurfaceRecipe = cva(['w-[280px] max-w-[var(--available-width)]']);

/**
 * The option list. Capped at 304px, which is Menu's cap and the panel description's, then scrolls.
 * The cap is not shipped by `Menu` yet (Guidelines: "Menu's 304px scroll cap is not shipped"), so
 * this is the first place it exists in code. `--available-height` keeps a short viewport from
 * clipping the footer.
 */
export const filterListRecipe = cva([
  'ds-popup-scroll flex flex-col gap-[2px]',
  'max-h-[min(--spacing(76),var(--available-height,100vh))] overflow-y-auto overscroll-contain',
]);

/**
 * One option row.
 *
 * `multiSelectItemRecipe` and `selectItemRecipe` are written against Base UI's list, which sets
 * `data-highlighted` under the pointer and the arrow keys. This row is a native `<label>` around a
 * real input, so nothing sets it: the component writes the attribute itself from pointer and focus,
 * and the recipes are reused as they are. That is what keeps these rows identical to the select's by
 * construction rather than by matching numbers.
 *
 * The one addition is the keyboard ring. A select's rows are never individually focusable, so its
 * `focusRing('selectItem')` never fires; these are, and a tint alone is not a focus indicator. The
 * ring is the same shadow with `has-[:focus-visible]` in place of `focus-visible`, because focus
 * lands on the hidden input inside the row.
 */
export const filterOptionFocusRecipe = cva([
  focusRing('selectItem').replaceAll('focus-visible:', 'has-[:focus-visible]:'),
]);

/** The tick, always mounted and faded by the row's own `data-selected`, on the box's clock. */
export const filterTickRecipe = cva([
  'pointer-events-none absolute inset-[-1px] block',
  'opacity-0 group-data-[selected]/msi:opacity-100',
  'transition-opacity duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  'text-[var(--ds-checkbox-glyph)]',
  'group-data-[disabled]/msi:text-[var(--ds-checkbox-glyph-disabled)]',
  '[&_svg]:size-full',
]);

/** A single-select row's tick, at the right edge, drawn only on the chosen row. */
export const filterSingleTickRecipe = cva(['group-data-[selected]/si:visible invisible']);

/** The hidden input. Real, so Tab, Space and the arrow keys are the platform's. */
export const filterInputRecipe = cva(['peer absolute size-px overflow-hidden opacity-0']);

/** The footer: the count on the left, the buttons on the right. */
export const filterFooterRecipe = cva(['flex items-center gap-2 p-2']);

export const filterCountRecipe = cva(['min-w-0 flex-1 truncate text-body-sm text-[var(--foreground-secondary)]']);

/** The neutral message. A row rather than an Alert: nothing is broken, the group is just too small. */
export const filterWarningRecipe = cva([
  'flex items-start gap-2 px-[10px] py-2 text-body-sm text-[var(--foreground-secondary)] empty:hidden',
]);

/** Skeleton rows keep the option row's own padding, so the panel does not jump when options arrive. */
export const filterSkeletonRowRecipe = cva(['flex items-center gap-2 px-[12px] py-[8px]']);
